import { HeaderButtonProps } from "@omni/types/components/props/headerButton.props";
import { hasWindow } from "@omni/utils/dom.utils";
import { cn } from "@omni/utils/styles";
import {
    MouseEvent,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useState
} from "react";

import { HeaderButtonContextProvider } from "@/components/Navigation/Buttons/HeaderButton/context/headerButton.context";
import { HeaderButtonActivatedEvent } from "@/services/windowEvents/events/headerButtonActivated.event";
import { WindowEvent } from "@/services/windowEvents/windowEvent.service";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

export type HeaderButtonBaseProps = HeaderButtonProps & {
    hideChevron?: boolean;
    withOverlay?: boolean;
    iconPrefix?: string;
    className?: string;
    menuElement?: ReactElement<{ closeCallback?: () => void }> | false | null;
};

type Props = HeaderButtonBaseProps & {
    children: ReactNode;
};

export const Base = ({
    // HeaderButton props
    text,
    ariaLabel,
    hiddenText = false,
    icon,
    link,
    invert,
    invertMobile,
    showMenuOnHover,
    alerts,

    // HeaderButtonBase props
    hideChevron = false,
    withOverlay = false,
    iconPrefix = "las",
    className,
    menuElement,

    // Local props
    children
}: Props) => {
    const [isActive, setActiveState] = useState<boolean>(false);
    const [preventBlur, setPreventBlur] = useState<boolean>(false);

    const setActive = useCallback((active: boolean = true) => {
        if (active && hasWindow()) {
            window?.dispatchEvent(new HeaderButtonActivatedEvent());
        }

        setActiveState(active);
    }, []);

    const handleOnActivated = useCallback(() => {
        setActive(false);
    }, [setActive]);

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: () => setActive(false)
        }
    ]);

    const handleOnCloseMenu = useCallback(() => {
        setActive(false);
        setPreventBlur(false);
    }, [setActive]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            WindowEvent.HeaderButtonActivated,
            handleOnActivated,
            {
                signal: abortController.signal
            }
        );
        window.addEventListener("keydown", handleOnKeyDown, {
            signal: abortController.signal
        });

        return () => {
            abortController.abort();
        };
    }, [handleOnKeyDown, handleOnActivated]);

    const onMouseEnter = () => {
        if (showMenuOnHover && menuElement) {
            setActive();
        }
        setPreventBlur(true);
    };

    const onMouseLeave = () => {
        if (showMenuOnHover && menuElement) {
            setActive(false);
        }
        setPreventBlur(false);
    };

    const handleOnClick = (event: MouseEvent) => {
        if (!showMenuOnHover && menuElement) {
            event.preventDefault();
            setActive(!isActive);
            setPreventBlur(true);
        }
    };

    const onBlur = () => {
        if (!preventBlur) {
            setActive(false);
        }
    };

    const overlayOpen = !!menuElement && isActive;

    return (
        <HeaderButtonContextProvider
            text={text}
            ariaLabel={ariaLabel}
            hiddenText={hiddenText}
            icon={icon}
            link={link}
            invert={!!invert}
            invertMobile={!!invertMobile}
            showMenuOnHover={showMenuOnHover}
            alerts={alerts}
            hideChevron={hideChevron}
            withOverlay={withOverlay}
            iconPrefix={iconPrefix}
            menuElement={menuElement}
            isActive={isActive}
            isOverlayOpen={overlayOpen}
            onClick={handleOnClick}
            onCloseMenu={handleOnCloseMenu}
        >
            <div
                className={cn(
                    "tw:relative",
                    {
                        "tw:layer-modal/[1]": isActive
                    },
                    className
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onBlur={onBlur}
            >
                {children}
            </div>
        </HeaderButtonContextProvider>
    );
};
